import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { orcamentosApi, servicosApi, leadsApi, clientesApi, cidadesApi } from '../lib/api';
import {
  formatCurrency,
  formatDate,
  generateWhatsAppMessageOrcamento,
  copyToClipboard,
  ORCAMENTO_STATUS_OPTIONS,
  getOrcamentoStatusColor,
  PERIODOS_ATENDIMENTO
} from '../lib/utils';
import { CidadeSelectOptions, ZonaBairroFields } from '../components/CidadeSelectOptions';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ServicoPicker } from '../components/ui/ServicoPicker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FileText, MessageCircle, CheckCircle2 } from 'lucide-react';
import useScrollRestoration from '../hooks/useScrollRestoration';

const emptyItemOrc = () => ({ servico_id: '', servico_nome: '', quantidade: 1, valor_unitario: 0 });

const emptyOrcamento = {
  cliente_nome: '',
  cliente_telefone: '',
  cliente_endereco: '',
  cidade_atendimento: '',
  zona_atendimento: '',
  bairro_atendimento: '',
  itens: [emptyItemOrc()],
  descricao_servico: '',
  validade: '',
  observacoes: '',
  periodo_atendimento: '',
  status: 'Em andamento',
  desconto_percentual: 0
};

const Orcamentos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orcamentos, setOrcamentos] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFecharDialog, setShowFecharDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ ...emptyOrcamento });
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [tab, setTab] = useState(() => sessionStorage.getItem('orcamentos:tab') || 'ativos');
  useScrollRestoration(`orcamentos:${tab}`, !loading);
  const setTabPersist = (t) => { sessionStorage.setItem('orcamentos:tab', t); setTab(t); };
  const [pendingLeadId, setPendingLeadId] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [cidadesList, setCidadesList] = useState([]);
  const [clienteExistenteId, setClienteExistenteId] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [orcRes, servRes, cliRes, cidadesRes] = await Promise.all([
        orcamentosApi.list(),
        servicosApi.list(),
        clientesApi.list(),
        cidadesApi.list(),
      ]);
      setOrcamentos(orcRes.data);
      setServicos(servRes.data);
      setClientes(cliRes.data);
      setCidadesList(cidadesRes.data);
    } catch (error) {
      console.error('Error loading orcamentos:', error);
      toast.error('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-open pre-filled form when navigated from a Lead
  useEffect(() => {
    const fromLead = location.state?.fromLead;
    if (fromLead && !loading) {
      const hoje = new Date();
      hoje.setDate(hoje.getDate() + 7);
      const validadeDefault = hoje.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      setSelected(null);
      setFormData({
        ...emptyOrcamento,
        itens: [emptyItemOrc()],
        cliente_nome: fromLead.cliente_nome || '',
        cliente_telefone: fromLead.cliente_telefone || '',
        cidade_atendimento: fromLead.cidade_atendimento || '',
        zona_atendimento: fromLead.zona_atendimento || '',
        bairro_atendimento: fromLead.bairro_atendimento || '',
        observacoes: fromLead.observacoes || '',
        validade: validadeDefault
      });
      setPendingLeadId(fromLead.lead_id || null);
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: null });
      toast.info('Formulário preenchido a partir do lead');
    }
  }, [location.state, location.pathname, loading, navigate]);

  const handleOpenForm = (orcamento = null) => {
    if (orcamento) {
      setSelected(orcamento);
      let itens = [emptyItemOrc()];
      if (Array.isArray(orcamento.itens) && orcamento.itens.length > 0) {
        itens = orcamento.itens.map((it) => ({
          servico_id: it.servico_id || '',
          servico_nome: it.servico_nome || '',
          quantidade: Number(it.quantidade || 1),
          valor_unitario: Number(it.valor_unitario || 0)
        }));
      } else if (orcamento.servico_id) {
        itens = [{
          servico_id: orcamento.servico_id,
          servico_nome: orcamento.descricao_servico || '',
          quantidade: 1,
          valor_unitario: Number(orcamento.valor || 0)
        }];
      }
      setFormData({
        cliente_nome: orcamento.cliente_nome,
        cliente_telefone: orcamento.cliente_telefone,
        cliente_endereco: orcamento.cliente_endereco || '',
        cidade_atendimento: orcamento.cidade_atendimento || '',
        zona_atendimento: orcamento.zona_atendimento || '',
        bairro_atendimento: orcamento.bairro_atendimento || '',
        itens,
        descricao_servico: orcamento.descricao_servico || '',
        validade: orcamento.validade,
        observacoes: orcamento.observacoes || '',
        periodo_atendimento: orcamento.periodo_atendimento || '',
        status: orcamento.status,
        desconto_percentual: Number(orcamento.desconto_percentual || 0)
      });
    } else {
      setSelected(null);
      const hoje = new Date();
      hoje.setDate(hoje.getDate() + 7);
      const validadeDefault = hoje.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      setFormData({ ...emptyOrcamento, itens: [emptyItemOrc()], validade: validadeDefault });
    }
    setShowForm(true);
    setClienteExistenteId('');
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelected(null);
    setFormData({ ...emptyOrcamento, itens: [emptyItemOrc()] });
    setPendingLeadId(null);
    setClienteExistenteId('');
  };

  const updateOrcItem = (idx, patch) => {
    setFormData((prev) => {
      const nextItens = prev.itens.map((it, i) => (i === idx ? { ...it, ...patch } : it));
      return { ...prev, itens: nextItens };
    });
  };

  const handleOrcItemServicoChange = (idx, servicoId) => {
    const s = servicos.find((x) => x.id === servicoId);
    updateOrcItem(idx, {
      servico_id: servicoId,
      servico_nome: s?.nome || '',
      valor_unitario: s?.preco_base || 0
    });
  };

  const addOrcItem = () => {
    setFormData((prev) => ({ ...prev, itens: [...prev.itens, emptyItemOrc()] }));
  };

  const removeOrcItem = (idx) => {
    setFormData((prev) => {
      const next = prev.itens.filter((_, i) => i !== idx);
      return { ...prev, itens: next.length ? next : [emptyItemOrc()] };
    });
  };

  const computeOrcTotal = () => {
    return formData.itens.reduce(
      (sum, it) => sum + (Number(it.valor_unitario || 0) * Number(it.quantidade || 1)),
      0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_nome.trim() || !formData.cliente_telefone.trim()) {
      toast.error('Nome e telefone do cliente são obrigatórios');
      return;
    }
    const itensValidos = formData.itens.filter((it) => it.servico_id);
    if (itensValidos.length === 0) {
      toast.error('Adicione ao menos 1 item com serviço selecionado');
      return;
    }
    if (!formData.validade) {
      toast.error('Informe a validade do orçamento');
      return;
    }
    setSaving(true);
    try {
      const { descricao_servico: _leg1, ...rest } = formData;
      const payload = {
        ...rest,
        descricao_servico: itensValidos.map((it) =>
          `${it.servico_nome}${Number(it.quantidade || 1) > 1 ? ` (x${it.quantidade})` : ''}`
        ).join(' + '),
        itens: itensValidos.map((it) => ({
          servico_id: it.servico_id,
          servico_nome: it.servico_nome,
          quantidade: Math.max(0.01, Number(it.quantidade || 1)),
          valor_unitario: Number(it.valor_unitario || 0)
        }))
      };
      if (selected) {
        await orcamentosApi.update(selected.id, payload);
        toast.success('Orçamento atualizado!');
      } else {
        const res = await orcamentosApi.create(payload);
        toast.success('Orçamento criado!');
        if (pendingLeadId && res?.data?.id) {
          try {
            await leadsApi.marcarConvertido(pendingLeadId, res.data.id);
            toast.success('Lead marcado como Convertido');
          } catch (linkErr) {
            console.error('Error linking lead:', linkErr);
          }
        }
      }
      handleCloseForm();
      loadData();
    } catch (error) {
      console.error('Error saving orcamento:', error);
      toast.error('Erro ao salvar orçamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await orcamentosApi.delete(selected.id);
      toast.success('Orçamento excluído!');
      setShowDeleteDialog(false);
      setSelected(null);
      loadData();
    } catch (error) {
      console.error('Error deleting orcamento:', error);
      toast.error('Erro ao excluir orçamento');
    }
  };

  const handleCopyWhatsApp = async (orcamento) => {
    const message = generateWhatsAppMessageOrcamento(orcamento);
    const ok = await copyToClipboard(message);
    if (ok) toast.success('Mensagem do orçamento copiada!');
    else toast.error('Erro ao copiar mensagem');
  };

  const handleStatusChange = async (orcamento, newStatus) => {
    try {
      await orcamentosApi.update(orcamento.id, { status: newStatus });
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleFecharConfirm = async () => {
    if (!selected) return;
    try {
      const res = await orcamentosApi.fechar(selected.id);
      setShowFecharDialog(false);
      navigate('/sistema/agendamento', {
        state: {
          fromOrcamento: {
            cliente_id: res.data.cliente_id,
            cliente_nome: res.data.cliente_nome,
            cliente_telefone: res.data.cliente_telefone,
            cliente_endereco: res.data.cliente_endereco,
            itens: res.data.itens,
            servico_id: res.data.servico_id,
            descricao_servico: res.data.descricao_servico,
            valor: res.data.valor,
            cidade_atendimento: res.data.cidade_atendimento,
            zona_atendimento: res.data.zona_atendimento || '',
            bairro_atendimento: res.data.bairro_atendimento || '',
            periodo_atendimento: res.data.periodo_atendimento,
            observacoes: res.data.observacoes,
            orcamento_id: res.data.orcamento_id
          }
        }
      });
    } catch (error) {
      console.error('Error closing orcamento:', error);
      toast.error('Erro ao fechar orçamento');
    }
  };

  // Tabs "Ativos" x "Perdidos" + ordenação por status
  const STATUS_ORDER_ATIVOS = { 'Em andamento': 0, 'Aguardando resposta': 1, 'Aprovado': 2 };
  const isPerdidoOrc = (o) => o.status === 'Negado' || o.status === 'Expirado';
  const orcamentosByTab = useMemo(() => {
    return orcamentos.filter((o) => tab === 'perdidos' ? isPerdidoOrc(o) : !isPerdidoOrc(o));
  }, [orcamentos, tab]);
  const filtered = useMemo(() => {
    const base = statusFilter === 'all' ? orcamentosByTab : orcamentosByTab.filter((o) => o.status === statusFilter);
    if (tab === 'ativos') {
      return [...base].sort((a, b) => {
        const oa = STATUS_ORDER_ATIVOS[a.status] ?? 99;
        const ob = STATUS_ORDER_ATIVOS[b.status] ?? 99;
        if (oa !== ob) return oa - ob;
        const va = a.validade || '9999-12-31';
        const vb = b.validade || '9999-12-31';
        if (va !== vb) return va.localeCompare(vb);
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
    }
    // perdidos: mais recente primeiro
    return [...base].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [orcamentosByTab, statusFilter, tab]);
  const countsOrcAtivos = orcamentos.filter((o) => !isPerdidoOrc(o)).length;
  const countsOrcPerdidos = orcamentos.filter(isPerdidoOrc).length;

  return (
    <div className="space-y-6" data-testid="orcamentos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Orçamentos
          </h1>
          <p className="text-gray-500 mt-1">
            Crie e acompanhe orçamentos. Quando aprovado, gere a Ordem de Serviço.
          </p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-orcamento-btn">
          <Plus size={18} className="mr-2" />
          Novo Orçamento
        </Button>
      </div>

      {/* Tabs Ativos / Perdidos */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTabPersist('ativos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'ativos' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-orc-ativos"
        >
          Ativos <span className="ml-1 text-xs text-gray-400">({countsOrcAtivos})</span>
        </button>
        <button
          type="button"
          onClick={() => setTabPersist('perdidos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'perdidos' ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-orc-perdidos"
        >
          Perdidos <span className="ml-1 text-xs text-gray-400">({countsOrcPerdidos})</span>
        </button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
            <FileText size={20} />
            Lista de Orçamentos
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="filter-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {ORCAMENTO_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum orçamento encontrado
            </div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th className="hidden lg:table-cell">Telefone</th>
                    <th className="hidden xl:table-cell">Endereço</th>
                    <th>Serviço</th>
                    <th>Valor</th>
                    <th className="hidden lg:table-cell">Validade</th>
                    <th>Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => (
                    <tr key={o.id} data-testid={`orcamento-row-${o.id}`}>
                      <td className="font-medium" data-label="Cliente">{o.cliente_nome}</td>
                      <td className="hidden lg:table-cell" data-label="Telefone">{o.cliente_telefone}</td>
                      <td className="cell-ellipsis hidden xl:table-cell" title={o.cliente_endereco || '—'} data-label="Endereço">
                        {o.cliente_endereco || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="cell-ellipsis" title={o.descricao_servico} data-label="Serviço">
                        {o.descricao_servico}
                      </td>
                      <td data-label="Valor">{formatCurrency(o.valor)}</td>
                      <td className="hidden lg:table-cell" data-label="Validade">{formatDate(o.validade)}</td>
                      <td data-label="Status">
                        <Select
                          value={o.status}
                          onValueChange={(v) => handleStatusChange(o, v)}
                        >
                          <SelectTrigger
                            className="h-8 w-28 lg:w-40 px-2 text-xs"
                            style={{
                              borderColor: getOrcamentoStatusColor(o.status),
                              color: getOrcamentoStatusColor(o.status)
                            }}
                            data-testid={`status-select-${o.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ORCAMENTO_STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td data-label="Ações">
                        <div className="flex items-center gap-1 flex-nowrap justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyWhatsApp(o)}
                            title="Copiar mensagem do orçamento"
                            data-testid={`whatsapp-orcamento-${o.id}`}
                          >
                            <MessageCircle size={16} className="text-green-600" />
                          </Button>
                          {o.status !== 'Aprovado' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelected(o);
                                setShowFecharDialog(true);
                              }}
                              title="Fechar e gerar OS"
                              data-testid={`fechar-orcamento-${o.id}`}
                            >
                              <CheckCircle2 size={16} className="text-blue-600" />
                            </Button>
                          )}
                          {o.status === 'Aprovado' && o.ordem_servico_id && (
                            <Badge
                              variant="outline"
                              className="text-xs"
                              style={{ borderColor: '#10B981', color: '#10B981' }}
                              title="OS gerada"
                            >
                              OS gerada
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(o)}
                            data-testid={`edit-orcamento-${o.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelected(o);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-orcamento-${o.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selected ? 'Editar Orçamento' : 'Novo Orçamento'}
            </DialogTitle>
            <DialogDescription>
              Informe nome e telefone do cliente, descrição do serviço, valor e validade.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!selected && clientes.length > 0 && (
              <div className="space-y-1 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-testid="usar-cliente-cadastrado">
                <Label className="text-emerald-800 font-semibold text-sm">
                  Usar cliente já cadastrado (opcional)
                </Label>
                <p className="text-xs text-gray-600 mb-1">
                  Selecione um cliente da sua lista para preencher nome, telefone e endereço automaticamente.
                </p>
                <Select
                  value={clienteExistenteId || '__none__'}
                  onValueChange={(v) => {
                    if (v === '__none__') {
                      setClienteExistenteId('');
                      return;
                    }
                    const c = clientes.find((x) => x.id === v);
                    if (!c) return;
                    setClienteExistenteId(v);
                    const end = c.endereco || {};
                    const partes = [end.rua, end.numero && `nº ${end.numero}`, end.complemento, end.bairro, end.cidade && end.estado ? `${end.cidade}/${end.estado}` : (end.cidade || end.estado)]
                      .filter(Boolean).join(', ');
                    setFormData((prev) => ({
                      ...prev,
                      cliente_nome: c.nome || '',
                      cliente_telefone: c.telefone || '',
                      cliente_endereco: partes || prev.cliente_endereco,
                      cidade_atendimento: end.cidade || prev.cidade_atendimento,
                    }));
                    toast.success(`Cliente "${c.nome}" carregado`);
                  }}
                >
                  <SelectTrigger data-testid="select-cliente-cadastrado">
                    <SelectValue placeholder="Novo cliente (preencher manualmente)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Novo cliente (preencher manualmente) —</SelectItem>
                    {clientes
                      .slice()
                      .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''))
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`opt-cli-${c.id}`}>
                          {c.nome} — {c.telefone}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cliente_nome">Nome do cliente *</Label>
                <Input
                  id="cliente_nome"
                  value={formData.cliente_nome}
                  onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                  placeholder="Ex: Maria Silva"
                  required
                  data-testid="orcamento-nome-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cliente_telefone">Telefone *</Label>
                <Input
                  id="cliente_telefone"
                  value={formData.cliente_telefone}
                  onChange={(e) => setFormData({ ...formData, cliente_telefone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  required
                  data-testid="orcamento-telefone-input"
                />
              </div>
            </div>

            <div className="space-y-2 rounded-lg border p-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Itens do Orçamento *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addOrcItem}
                  data-testid="add-orc-item-btn"
                >
                  <Plus size={14} className="mr-1" /> Adicionar item
                </Button>
              </div>
              <div className="space-y-2">
                {formData.itens.map((it, idx) => {
                  const sub = Number(it.valor_unitario || 0) * Number(it.quantidade || 1);
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded-md bg-white border" data-testid={`orc-item-${idx}`}>
                      <div className="col-span-12 md:col-span-5 space-y-1">
                        <Label className="text-xs">Serviço</Label>
                        <ServicoPicker
                          servicos={servicos}
                          value={it.servico_id || ''}
                          onChange={(v) => handleOrcItemServicoChange(idx, v)}
                          placeholder="Selecione o serviço"
                          testId={`orc-item-servico-select-${idx}`}
                        />
                      </div>
                      {(() => {
                        const isM2 = servicos.find((s) => s.id === it.servico_id)?.unidade === 'm2';
                        return (
                          <div className="col-span-4 md:col-span-2 space-y-1">
                            <Label className="text-xs">{isM2 ? 'm²' : 'Qtde'}</Label>
                            <Input
                              type="number"
                              min={isM2 ? '0.1' : '1'}
                              step={isM2 ? '0.1' : '1'}
                              value={it.quantidade}
                              onChange={(e) => updateOrcItem(idx, { quantidade: isM2 ? Math.max(0.1, parseFloat(e.target.value) || 0.1) : Math.max(1, parseInt(e.target.value, 10) || 1) })}
                              data-testid={`orc-item-qtd-${idx}`}
                            />
                          </div>
                        );
                      })()}
                      <div className="col-span-5 md:col-span-2 space-y-1">
                        <Label className="text-xs">Vlr unit.</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.valor_unitario}
                          onChange={(e) => updateOrcItem(idx, { valor_unitario: parseFloat(e.target.value) || 0 })}
                          data-testid={`orc-item-vunit-${idx}`}
                        />
                      </div>
                      <div className="col-span-3 md:col-span-2 space-y-1">
                        <Label className="text-xs">Subtotal</Label>
                        <div className="h-9 flex items-center px-2 rounded-md bg-gray-100 text-sm font-medium text-emerald-700">
                          {formatCurrency(sub)}
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-1 flex md:justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOrcItem(idx)}
                          disabled={formData.itens.length === 1}
                          title="Remover item"
                          data-testid={`orc-item-remove-${idx}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-sm">
                <div className="flex items-center gap-2">
                  <Label htmlFor="desconto-pct-orc" className="text-xs text-gray-500">Desconto (%)</Label>
                  <Input
                    id="desconto-pct-orc"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    className="w-24 h-8"
                    value={formData.desconto_percentual || 0}
                    onChange={(e) => setFormData({ ...formData, desconto_percentual: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                    data-testid="orc-desconto-input"
                  />
                </div>
                <div className="text-right">
                  {Number(formData.desconto_percentual || 0) > 0 && (
                    <div className="text-xs text-gray-400">
                      <span className="line-through">{formatCurrency(computeOrcTotal())}</span>
                      <span className="ml-1 text-red-500">−{String(formData.desconto_percentual).replace('.', ',')}%</span>
                    </div>
                  )}
                  <span className="text-gray-500 mr-2">Total:</span>
                  <span className="font-bold text-emerald-700 text-lg" data-testid="orc-total">
                    {formatCurrency(Math.round(computeOrcTotal() * (1 - Math.max(0, Math.min(100, Number(formData.desconto_percentual || 0))) / 100) * 100) / 100)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="cliente_endereco">Endereço do cliente</Label>
              <Input
                id="cliente_endereco"
                value={formData.cliente_endereco}
                onChange={(e) => setFormData({ ...formData, cliente_endereco: e.target.value })}
                placeholder="Rua, número, bairro, cidade"
                data-testid="orcamento-endereco-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cidade_atendimento">Cidade</Label>
                <Select
                  value={formData.cidade_atendimento || undefined}
                  onValueChange={(v) => setFormData({ ...formData, cidade_atendimento: v, zona_atendimento: '' })}
                >
                  <SelectTrigger data-testid="orcamento-cidade-select">
                    <SelectValue placeholder="Selecione a cidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <CidadeSelectOptions cidades={cidadesList} currentValue={formData.cidade_atendimento} />
                  </SelectContent>
                </Select>
              </div>
              <ZonaBairroFields
                cidades={cidadesList}
                cidade={formData.cidade_atendimento}
                zona={formData.zona_atendimento}
                bairro={formData.bairro_atendimento}
                onZonaChange={(v) => setFormData({ ...formData, zona_atendimento: v })}
                onBairroChange={(v) => setFormData({ ...formData, bairro_atendimento: v })}
                testPrefix="orcamento"
              />
              <div className="space-y-1">
                <Label htmlFor="periodo_atendimento">Período</Label>
                <Select
                  value={formData.periodo_atendimento || undefined}
                  onValueChange={(v) => setFormData({ ...formData, periodo_atendimento: v })}
                >
                  <SelectTrigger data-testid="orcamento-periodo-select">
                    <SelectValue placeholder="Selecione o período" />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODOS_ATENDIMENTO.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label} ({p.horario})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="validade">Validade *</Label>
                <Input
                  id="validade"
                  type="date"
                  value={formData.validade}
                  onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                  required
                  data-testid="orcamento-validade-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger data-testid="orcamento-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORCAMENTO_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Observações adicionais (opcional)"
                rows={2}
                data-testid="orcamento-obs-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-orcamento-btn">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Orçamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-orcamento-btn">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fechar (Generate OS) Confirmation */}
      <AlertDialog open={showFecharDialog} onOpenChange={setShowFecharDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar orçamento e gerar OS?</AlertDialogTitle>
            <AlertDialogDescription>
              O cliente será cadastrado (caso ainda não exista) e você será levado ao formulário
              de Ordem de Serviço com nome, telefone e valor já preenchidos. Após salvar a OS, o
              orçamento será marcado como &quot;Aprovado&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleFecharConfirm} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-fechar-orcamento-btn">
              Continuar para OS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Orcamentos;
